/**
 * The console's information architecture, matching the imported design's three
 * nav groups. This is the single source for ids, labels and paths — the
 * sidebar, the breadcrumb and the router all read from here so a renamed screen
 * cannot end up with three different names.
 */

export const SCREEN_IDS = [
  'overview',
  'users',
  'gyms',
  'foods',
  'foodrequests',
  'catalogchanges',
  'feedback',
  'exercises',
  'exerciserequests',
  'gymcatalog',
  'planops',
  'patterns',
  'aiops',
  'aispend',
  'jobs',
  'notifications',
  'rhythm',
  'broadcasts',
  'waittips',
  'billing',
  'promotions',
  'switches',
  'console',
  'activity',
] as const;

export type ScreenId = (typeof SCREEN_IDS)[number];

/**
 * The queues a nav badge counts.
 *
 * A key rather than a number: the count is live data, and baking it into this
 * module would make a config file something that has to be re-rendered.
 * `SidebarContent` resolves the key against whatever it has been handed, and
 * an item whose count has not arrived simply shows no badge.
 */
export const NAV_BADGES = [
  'foodsToReview',
  'openFoodRequests',
  'openFeedback',
  'failedPlanRuns',
  'gymCatalogWaiting',
  'openExerciseRequests',
] as const;
export type NavBadgeKey = (typeof NAV_BADGES)[number];
export type NavBadgeCounts = Partial<Record<NavBadgeKey, number>>;

export interface NavItemConfig {
  readonly id: ScreenId;
  readonly label: string;
  readonly path: string;
  readonly badge?: NavBadgeKey;
  /** Marks a screen that did not exist last time an operator looked. */
  readonly isNew?: boolean;
}

export interface NavGroupConfig {
  readonly label: string;
  readonly items: readonly NavItemConfig[];
}

/**
 * Keyed by id rather than a flat array so lookups are O(1) and exhaustiveness is
 * checked by the compiler: omit a screen and `satisfies` fails here, not at
 * runtime in the breadcrumb.
 */
export const NAV_ITEMS = {
  overview: { id: 'overview', label: 'Overview', path: '/' },
  users: { id: 'users', label: 'Users', path: '/users' },
  gyms: { id: 'gyms', label: 'Gyms', path: '/gyms', isNew: true },
  foods: { id: 'foods', label: 'Food catalog', path: '/foods', badge: 'foodsToReview' },
  foodrequests: {
    id: 'foodrequests',
    label: 'Food requests',
    path: '/food-requests',
    badge: 'openFoodRequests',
  },
  catalogchanges: { id: 'catalogchanges', label: 'Catalog changes', path: '/catalog-changes', isNew: true },
  feedback: { id: 'feedback', label: 'Feedback', path: '/feedback', badge: 'openFeedback' },
  exercises: { id: 'exercises', label: 'Exercise library', path: '/exercises' },
  exerciserequests: {
    id: 'exerciserequests',
    label: 'Exercise requests',
    path: '/exercise-requests',
    badge: 'openExerciseRequests',
    isNew: true,
  },
  gymcatalog: {
    id: 'gymcatalog',
    label: 'From gyms',
    path: '/gym-catalog',
    badge: 'gymCatalogWaiting',
    isNew: true,
  },
  planops: { id: 'planops', label: 'Plan operations', path: '/plan-operations', badge: 'failedPlanRuns' },
  patterns: { id: 'patterns', label: 'Meal patterns', path: '/meal-patterns' },
  aiops: { id: 'aiops', label: 'AI operations', path: '/ai-operations' },
  aispend: { id: 'aispend', label: 'Spend by user', path: '/ai-spend' },
  jobs: { id: 'jobs', label: 'Jobs and queues', path: '/jobs' },
  notifications: { id: 'notifications', label: 'Notifications', path: '/notifications' },
  rhythm: { id: 'rhythm', label: 'Rhythm', path: '/rhythm', isNew: true },
  broadcasts: { id: 'broadcasts', label: 'Broadcasts', path: '/broadcasts' },
  waittips: { id: 'waittips', label: 'Wait tips', path: '/wait-tips' },
  billing: { id: 'billing', label: 'Billing', path: '/billing' },
  promotions: { id: 'promotions', label: 'Promotions', path: '/promotions', isNew: true },
  switches: { id: 'switches', label: 'Feature switches', path: '/feature-switches' },
  console: { id: 'console', label: 'API console', path: '/api-console' },
  activity: { id: 'activity', label: 'Admin activity', path: '/activity' },
} as const satisfies Record<ScreenId, NavItemConfig>;

/**
 * `as const satisfies` rather than a `readonly NavGroupConfig[]` annotation: the
 * annotation would widen every `path` back to `string`, and TanStack Router
 * checks `<Link to>` against the literal paths in the route tree. Keeping them
 * literal turns a mistyped nav path into a compile error.
 */
export const NAV_GROUPS = [
  {
    label: 'Operate',
    items: [
      NAV_ITEMS.overview,
      NAV_ITEMS.users,
      NAV_ITEMS.gyms,
      NAV_ITEMS.foods,
      NAV_ITEMS.foodrequests,
      NAV_ITEMS.catalogchanges,
      NAV_ITEMS.feedback,
      NAV_ITEMS.exercises,
      NAV_ITEMS.exerciserequests,
      NAV_ITEMS.gymcatalog,
    ],
  },
  {
    label: 'Systems',
    items: [
      NAV_ITEMS.planops,
      NAV_ITEMS.patterns,
      NAV_ITEMS.aiops,
      NAV_ITEMS.aispend,
      NAV_ITEMS.jobs,
      NAV_ITEMS.notifications,
      NAV_ITEMS.rhythm,
      NAV_ITEMS.broadcasts,
      NAV_ITEMS.waittips,
      NAV_ITEMS.billing,
      NAV_ITEMS.promotions,
      NAV_ITEMS.switches,
    ],
  },
  {
    label: 'Developer',
    items: [NAV_ITEMS.console, NAV_ITEMS.activity],
  },
] as const satisfies readonly NavGroupConfig[];

/**
 * The concrete nav entries, with `path` still narrowed to its literal type.
 *
 * Intersected with the optional members rather than annotated as
 * `NavItemConfig`: the annotation would widen every `path` back to `string`
 * and TanStack Router would stop checking `<Link to>` against the route tree,
 * while the intersection leaves the literals intact and still lets the sidebar
 * read `badge` and `isNew` off an entry that does not declare them.
 */
export type NavItem = (typeof NAV_ITEMS)[ScreenId] & {
  readonly badge?: NavBadgeKey;
  readonly isNew?: boolean;
};

export function screenTitle(id: ScreenId): string {
  return NAV_ITEMS[id].label;
}

export function screenPath(id: ScreenId): string {
  return NAV_ITEMS[id].path;
}
